import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Mail, CheckCircle, XCircle, Clock, RefreshCw, Eye, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminEmailLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [templateFilter, setTemplateFilter] = useState('all');

  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [statusFilter, templateFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/email-logs?limit=100`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (templateFilter !== 'all') url += `&template=${templateFilter}`;
      
      const res = await axios.get(url, authHeaders);
      setLogs(res.data.logs || []);
    } catch (err) {
      console.error('Error fetching email logs:', err);
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/email-logs/stats`, authHeaders);
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching email stats:', err);
    }
  };

  const resendEmail = async (logId) => {
    try {
      await axios.post(`${API}/admin/email-logs/${logId}/resend`, {}, authHeaders);
      toast.success('Email resent successfully');
      fetchLogs();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend email');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" /> Sent</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" /> Failed</Badge>;
      case 'pending':
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTemplateLabel = (slug) => {
    const labels = {
      'welcome': 'Welcome',
      'trade_approved': 'Trade Approved',
      'trade_rejected': 'Trade Rejected',
      'trade_application_received': 'Trade Application',
      'order_confirmation': 'Order Confirmation',
      'order_shipped': 'Order Shipped',
      'order_delivered': 'Order Delivered',
      'invoice_sent': 'Invoice Sent',
      'payment_received': 'Payment Received',
      'estimate_sent': 'Estimate Sent',
      'password_reset': 'Password Reset',
      'low_stock_alert': 'Low Stock Alert'
    };
    return labels[slug] || slug;
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="email-logs-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-white">Email Logs</h1>
            <p className="text-zinc-400 text-sm">Track all sent emails and delivery status</p>
          </div>
          <Button variant="outline" onClick={() => { fetchLogs(); fetchStats(); }}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-sm">Total Sent</p>
                    <p className="text-2xl font-bold text-white">{stats.total}</p>
                  </div>
                  <Mail className="h-8 w-8 text-zinc-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-sm">Successful</p>
                    <p className="text-2xl font-bold text-green-500">{stats.sent}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-sm">Failed</p>
                    <p className="text-2xl font-bold text-red-500">{stats.failed}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-red-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-sm">Last 24h</p>
                    <p className="text-2xl font-bold text-white">{stats.last_24_hours}</p>
                  </div>
                  <Clock className="h-8 w-8 text-zinc-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-sm">Success Rate</p>
                    <p className="text-2xl font-bold text-[#78BE20]">{stats.success_rate}%</p>
                  </div>
                  <Send className="h-8 w-8 text-[#78BE20]" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-zinc-900 border-zinc-700">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          <Select value={templateFilter} onValueChange={setTemplateFilter}>
            <SelectTrigger className="w-48 bg-zinc-900 border-zinc-700">
              <SelectValue placeholder="Template" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Templates</SelectItem>
              <SelectItem value="welcome">Welcome</SelectItem>
              <SelectItem value="invoice_sent">Invoice Sent</SelectItem>
              <SelectItem value="payment_received">Payment Received</SelectItem>
              <SelectItem value="estimate_sent">Estimate Sent</SelectItem>
              <SelectItem value="order_confirmation">Order Confirmation</SelectItem>
              <SelectItem value="order_shipped">Order Shipped</SelectItem>
              <SelectItem value="trade_approved">Trade Approved</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Email Logs Table */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Email History</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-zinc-400">Loading...</div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8 text-zinc-400">
                <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No emails sent yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-3 px-4 text-zinc-400 text-sm font-medium">Recipient</th>
                      <th className="text-left py-3 px-4 text-zinc-400 text-sm font-medium">Subject</th>
                      <th className="text-left py-3 px-4 text-zinc-400 text-sm font-medium">Template</th>
                      <th className="text-left py-3 px-4 text-zinc-400 text-sm font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-zinc-400 text-sm font-medium">Sent At</th>
                      <th className="text-right py-3 px-4 text-zinc-400 text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-white font-medium">{log.to_name || 'Unknown'}</p>
                            <p className="text-zinc-400 text-sm">{log.to_email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-zinc-300 max-w-xs truncate">{log.subject}</p>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="text-xs">
                            {getTemplateLabel(log.template_slug)}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(log.status)}
                        </td>
                        <td className="py-3 px-4 text-zinc-400 text-sm">
                          {formatDate(log.sent_at)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex gap-2 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedLog(log); setViewDialogOpen(true); }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {log.status === 'failed' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => resendEmail(log.id)}
                              >
                                <RefreshCw className="h-4 w-4 mr-1" /> Resend
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* By Template Stats */}
        {stats?.by_template && stats.by_template.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">Emails by Template</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {stats.by_template.map((item) => (
                  <div key={item.template} className="bg-zinc-800 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-white">{item.count}</p>
                    <p className="text-zinc-400 text-sm">{getTemplateLabel(item.template)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* View Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Email Details</DialogTitle>
            </DialogHeader>
            {selectedLog && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-zinc-500 text-sm">Recipient</p>
                    <p className="text-white">{selectedLog.to_name || 'Unknown'}</p>
                    <p className="text-zinc-400 text-sm">{selectedLog.to_email}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Status</p>
                    <div className="mt-1">{getStatusBadge(selectedLog.status)}</div>
                  </div>
                </div>
                
                <div>
                  <p className="text-zinc-500 text-sm">Subject</p>
                  <p className="text-white">{selectedLog.subject}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-zinc-500 text-sm">Template</p>
                    <Badge variant="outline">{getTemplateLabel(selectedLog.template_slug)}</Badge>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Sent At</p>
                    <p className="text-zinc-300">{formatDate(selectedLog.sent_at)}</p>
                  </div>
                </div>
                
                {selectedLog.related_entity_type && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-zinc-500 text-sm">Related Entity</p>
                      <p className="text-zinc-300 capitalize">{selectedLog.related_entity_type}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm">Entity ID</p>
                      <p className="text-zinc-300 text-xs font-mono">{selectedLog.related_entity_id}</p>
                    </div>
                  </div>
                )}
                
                {selectedLog.error_message && (
                  <div className="bg-red-900/20 border border-red-800 rounded-lg p-3">
                    <p className="text-zinc-500 text-sm">Error Message</p>
                    <p className="text-red-400">{selectedLog.error_message}</p>
                  </div>
                )}
                
                {selectedLog.status === 'failed' && (
                  <Button
                    className="w-full"
                    onClick={() => { resendEmail(selectedLog.id); setViewDialogOpen(false); }}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" /> Resend Email
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
